import {Controller, useForm} from "react-hook-form";
import {
  useCompleteStripeRegistration,
  useLoginToStripe,
  useWithdrawalDetails,
} from "../../../../../../../react-query-hooks/payout";
import {useInitiateWithdrawal} from "@/features/withdrawals";
import {InitiateWithdrawalFormValues} from "../types";
import {initiateWithdrawalFormSchema} from "../validation-schema";
import {yupResolver} from "@hookform/resolvers/yup";
import {Alert, Button, Input, message} from "antd";
import {CustomModal} from "../../../../../../modal";
import {StyledModalContentWrapper} from "../../../../../../calls/upcoming-calls/call-card/call-card.style";
import {ErrorBoundary} from "react-error-boundary";
import {useCallback, useState} from "react";
import {useGetProfileContext} from "../../../../../../../context/profile/profile-context";
import {useRequestWithdrawal2FA} from "@/react-query-hooks/two-factor";
import {Withdrawal2FAStep} from "../withdrawal-2fa-step";
import {OTP_RESEND_COOLDOWN_SECONDS} from "../withdrawal-2fa-step/resend-countdown";

type TWithdrawalStep = "amount" | "twofa";

type PayoutWithdrawalModalProps = {
  handleModalCancel: () => void;
  handleModalOk: () => void;
  visibleState: boolean;
  stripeDetails: {
    isStripe: boolean;
    isStripeAndInactive: boolean;
    isStripeAndActive: boolean;
  };
};
export function PayoutWithdrawalModal({
  handleModalCancel,
  visibleState,
  handleModalOk,
  stripeDetails,
}: PayoutWithdrawalModalProps) {
  const completeStripeRegistrationQueryMutation = useCompleteStripeRegistration();
  const loginToStripeQueryMutation = useLoginToStripe();
  const isStripe = stripeDetails.isStripe;
  const isStripeAndInactive = stripeDetails.isStripeAndInactive;
  const isStripeAndActive = stripeDetails.isStripeAndActive;
  const initiateWithdrawalMutation = useInitiateWithdrawal();
  const {state: profileState} = useGetProfileContext();

  const [step, setStep] = useState<TWithdrawalStep>("amount");
  const [pendingAmount, setPendingAmount] = useState("");
  // Owned here rather than in the 2FA step so stepping back to the amount view
  // does not unmount the challenge and trigger a second OTP email.
  const [otpRequested, setOtpRequested] = useState(false);
  const [resendAvailableAt, setResendAvailableAt] = useState(0);
  const requestOtpMutation = useRequestWithdrawal2FA();

  const twoFactorMethod = profileState.profileData.twoFactorMethod;

  const [accountBalance, , connectedBank] = useWithdrawalDetails({
    connectBankUniqueIdentifier: "payout_withdrawal_modal",
  });

  const {
    handleSubmit,
    formState: {errors},
    watch,
    control,
    reset,
    setError,
  } = useForm<InitiateWithdrawalFormValues>({
    resolver: yupResolver(initiateWithdrawalFormSchema),
    mode: "onChange",
  });

  const resetModal = useCallback(() => {
    reset();
    setStep("amount");
    setPendingAmount("");
    setOtpRequested(false);
    setResendAvailableAt(0);
  }, [reset]);

  // Flagged before the request resolves so a quick Back/forward cannot fire a
  // second send; cleared on failure so the coach can retry.
  const requestWithdrawalOtp = useCallback(() => {
    setOtpRequested(true);
    requestOtpMutation.mutate(undefined, {
      onSuccess: () =>
        setResendAvailableAt(Date.now() + OTP_RESEND_COOLDOWN_SECONDS * 1000),
      onError: () => setOtpRequested(false),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCancel = useCallback(() => {
    resetModal();
    handleModalCancel();
  }, [handleModalCancel, resetModal]);
  const handleOk = useCallback(() => {
    resetModal();
    handleModalOk();
  }, [handleModalOk, resetModal]);

  const handleCompleteStripeRegistration = () => {
    if (isStripeAndInactive) {
      completeStripeRegistrationQueryMutation.mutate(undefined, {
        onSuccess: (data) => {
          if (data.loginUrl) {
            message.info("Redirecting to stripe....", 1, () => {
              setTimeout(() => {
                window.open(data.loginUrl!);
              }, 0);
            });
          }
        },
      });
    }
  };
  const handleLoginToStripe = () => {
    if (isStripeAndActive) {
      loginToStripeQueryMutation.mutate(undefined, {
        onSuccess: (data) => {
          message.info("Redirecting to stripe....", 1, () => {
            setTimeout(() => {
              window.open(data.link);
            }, 0);
          });
        },
      });
    }
  };

  // Rejects when the request fails so the 2FA step can clear its input and let
  // the coach retry — the mutation's onError already surfaces the reason.
  const submitWithdrawal = async (amount: string, twofaCode?: string) => {
    const data = await initiateWithdrawalMutation.mutateAsync({
      amount: Number(amount),
      ...(twofaCode ? {twofaCode} : {}),
    });

    // Handle Stripe redirect URL from v2 API
    if (data.isStripe && data.stripeRedirectUrl) {
      message.info("Redirecting to Stripe...", 1, () => {
        window.open(data.stripeRedirectUrl!, "_blank");
      });
      handleCancel();
      return;
    }
    message.success(
      "Your request has been submitted successfully! Your proceeds would be deposited to your bank within 24 hours"
    );
    handleCancel();
  };

  const onSubmit = async (values: InitiateWithdrawalFormValues) => {
    if (accountBalance.data) {
      if (Number(accountBalance.data.balance) <= Number(values.amount)) {
        setError("amount", {
          message: "Not enough funds",
        });
      } else {
        if (isStripe) {
          if (isStripeAndInactive) {
            handleCompleteStripeRegistration();
            return;
          }
          handleLoginToStripe();
        } else if (twoFactorMethod) {
          // Not gated on IS_TWO_FACTOR_FEATURE_ENABLED: the endpoint requires a
          // code whenever the account has 2FA on, so skipping this step sends
          // a withdrawal the backend rejects. `twoFactorMethod` is the server's
          // current answer — this profile comes from a react-query fetch that
          // isn't persisted, unlike the redux profile slice.
          // The withdrawal itself carries the 2FA code, so hold the amount and
          // collect the code before sending anything.
          setPendingAmount(values.amount);
          setStep("twofa");
          // One code per open modal - it authorises the coach, not the amount.
          if (twoFactorMethod === "email" && !otpRequested) {
            requestWithdrawalOtp();
          }
        } else {
          await submitWithdrawal(values.amount).catch(() => undefined);
        }
      }
    } else {
      message.error("Can't initiate withdrawal at the moment, please try again later");
    }
  };

  const isTwoFactorStep = step === "twofa";

  return (
    <CustomModal
      visibleState={visibleState}
      title={
        isTwoFactorStep
          ? "2-factor authentication"
          : "Withdraw your earnings into your account"
      }
      okFunc={handleOk}
      cancelFunc={handleCancel}
      footerContent={
        isTwoFactorStep
          ? null
          : [
              <Button size="small" key="cancel" type="default" onClick={handleCancel}>
                Cancel
              </Button>,
              <Button
                size="small"
                key="save"
                type="primary"
                onClick={handleSubmit(onSubmit)}
                disabled={
                  Object.keys(errors).length > 0 || Object.values(watch()).length < 1
                }
                loading={
                  initiateWithdrawalMutation.isLoading ||
                  loginToStripeQueryMutation.isLoading ||
                  completeStripeRegistrationQueryMutation.isLoading
                }
              >
                Withdraw earnings
              </Button>,
            ]
      }
    >
      <StyledModalContentWrapper>
        <ErrorBoundary
          FallbackComponent={({error, resetErrorBoundary}) => (
            <div style={{marginTop: "1.8rem"}}>
              <Alert
                message="Sorry, something went wrong."
                showIcon
                description={error.message}
                type="error"
                action={
                  <Button
                    onClick={() => {
                      resetErrorBoundary();
                    }}
                    size="small"
                    danger
                  >
                    Reset
                  </Button>
                }
              />
            </div>
          )}
        >
          {isTwoFactorStep ? (
            <Withdrawal2FAStep
              method={twoFactorMethod === "totp" ? "totp" : "email"}
              isSubmitting={initiateWithdrawalMutation.isLoading}
              isResending={requestOtpMutation.isLoading}
              resendAvailableAt={resendAvailableAt}
              onResend={requestWithdrawalOtp}
              onBack={() => setStep("amount")}
              onSubmit={(twofaCode) => submitWithdrawal(pendingAmount, twofaCode)}
            />
          ) : (
            <>
              <p>
                Your earnings will be sent to:
                <br />
                {connectedBank.data?.bank.bank_name} -{" "}
                {connectedBank.data?.bank.account_number} -{" "}
                {connectedBank.data?.bank.account_name}
              </p>
              <div style={{margin: "3.3rem 0"}}>
                <p>Amount:</p>
                <Controller
                  control={control}
                  name="amount"
                  render={({field: {onChange, value}}) => (
                    <>
                      <Input
                        id="amount"
                        status={errors.amount?.message && "error"}
                        size="large"
                        placeholder="Enter amount"
                        onChange={onChange}
                        value={value}
                      />
                      {errors.amount?.message && (
                        <div role="alert" className="ant-form-item-explain-error">
                          {errors.amount?.message}
                        </div>
                      )}
                    </>
                  )}
                />
                {connectedBank.data?.bank.country_name.toLowerCase() === "nigeria" && (
                  <p>NGN 50.00 will be deducted from your withdrawal amount</p>
                )}
              </div>
            </>
          )}
        </ErrorBoundary>
      </StyledModalContentWrapper>
    </CustomModal>
  );
}
